import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import {
  activateDeactivateRoles,
  createRole,
  deleteRole,
  fetchAllRoles,
  fetchRoleTableColumnNames,
  fetchTranslation,
  updateRoles,
  updateUserPreference,
} from "../../../slices/thunks";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import AddCommonForm from "../../../Components/Common/AddCommonForm/AddCommonForm";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import addRolesmanagementForm from "../../../Components/Common/addRolesmanagementForm/addRolesmanagementForm";
import { activateDeactivateCategories, createCategory, deleteCategory, fetchAllCategories, fetchCategoryTableColumnNames, fetchParentCategories, getDocumentsType, updateCategories } from "../../../slices/Category/thunk";
import AddCategoryForm from "../../../Components/Common/AddCategoryForm/AddCategoryForm";

const labels = [
  {
    id: 1,
    labelName: "Parent Category",
    fieldName: "select-box-parent",
    name: "ParentCategory",
    value: "ParentCategoryID",
    required: false,
    errorMessage: "Parent Category Is Required",
  },
  {
    id: 2,
    labelName: "DocumentsType",
    fieldName: "multiselect-dropdown",
    name: "DocumentType",
    value: "DocumentType",
    required: true,
    errorMessage: "DocumentsTypeRequired",
  },
  {
    id: 3,
    labelName: "CategoryNameArabic",
    fieldName: "text-box-translate",
    name: "CategoryNameAr",
    value: "CategoryNameAr",
    required: true,
    errorMessage: "Category Name Arabic Is Required",
    minLength: 2,
    minLengthErrorMessage: "Category Name Arabic must be at least 2 characters",
    maxLength: 20,
    maxLengthErrorMessage: "Category Name Arabic must be at most 20 characters",
    translateTo: "CategoryNameEn",
  },
  {
    id: 4,
    labelName: "CategoryNameEnglish",
    fieldName: "text-box",
    name: "CategoryNameEn",
      value: "CategoryNameEn",
      required: true,
      errorMessage: "Category Name English Is Required",
      minLength: 2,
      minLengthErrorMessage: "Category Name English must be at least 2 characters",
      maxLength: 20,
      maxLengthErrorMessage: "Category Name English must be at most 20 characters",
    },
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

  

function Categorymanagement() {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {user} = getAuthUser();

  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [category, setCategory] = useState();
  const [modalOpen, setModalOpen] = useState(false);  
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [lastTranslatedField, setLastTranslatedField] = useState(null); 

  
  const { categories, loading, error, tableColumnNames, Translations, translationLoading } = useSelector(
    (state) => ({
      categories: state.Category.categories,
      tableColumnNames: state.Category.tableColumnNames,
      loading: state.Category.loading,
      error: state.Category.error,
      Translations: state.Module.translations,
      translationLoading: state.Module.translationLoading, 
    })
  );

  // Compute permission
  const permission = (() => {
    // moduleId: 1 (Administration), pageId: 2 (Roles Management)
    return usePermissions(2, 6);
  })();

  
  const onAddClicksHandler = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
    setCategory({});
  };

  const onEditClickHandler = (category, value) => {
    const editableCategory = {
      ...category,
      CategoryNameEn: category.IsParentCategory ? category.ParentCategoryNameEn : category.CategoryNameEn,
      CategoryNameAr: category.IsParentCategory ? category.ParentCategoryNameAr : category.CategoryNameAr
    };
    
    setSelectedRow(editableCategory);
    setCategory(editableCategory);
    setIsAddOrEdit(value);
    setShow(true);
  };


  const onActiveOrDeactiveChange = (category, e) => {

    dispatch(activateDeactivateCategories({ ID: category.ID }));
  };

  const confirmDelete = () => {

    dispatch(deleteCategory({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onSubmitCategoryHandler = (values, { resetForm }) => {
    // Base object with common properties
    
    const categoryObj = {
      CategoryNameEn: values.CategoryNameEn.trim() || "",
      CategoryNameAr: values.CategoryNameAr.trim() || "",
      DocumentType: values.DocumentType || 0,
      ParentCategoryID: values.ParentCategory || 0, 
    };

    
    // If editing, include CategoryId and IsActive
    if (isAddOrEdit === "isEdit") {
      if (category && category.ID) {
        categoryObj.ID = category.ID;
      } 
    }
    
    if (isAddOrEdit === "isAdd") {
      dispatch(createCategory(categoryObj));
    } else {
      dispatch(updateCategories(categoryObj));
    }

    resetForm();
    setShow(false);    
  };

  const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
      toast.error(t("EnterTextToTranslate"));
      return;
    }
    setLastTranslatedField(sourceFieldName); 

    dispatch(fetchTranslation({ LangText: textToTranslate }))
      .unwrap() 
      .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
      })
      .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
      })
      .finally(() => {
        setLastTranslatedField(null); 
      });
  };


  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchCategoryTableColumnNames({
          module: "CATEGORY",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchCategoryTableColumnNames({
            module: "CATEGORY",
          })
        ).unwrap();
        await dispatch( fetchAllCategories());
        dispatch( fetchParentCategories());
        dispatch( getDocumentsType());
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Category")} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
              <Row className="g-4">
                    <Col className="col-sm-auto">
                      <div>
                        <Button
                          className="btn btn-soft-danger ms-2 me-2"
                          onClick={() => setDeleteModal(true)}
                          disabled={selectedIds.length === 0}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {permission.CanAdd && (
                          <Button
                            color="secondary"
                            className="add-btn me-1"
                            onClick={() => onAddClicksHandler("isAdd")}
                            id="create-btn"
                            disabled={loading}
                          >
                            <i className="ri-add-line align-bottom me-1 ms-1"></i>
                            {t("AddNew")}
                          </Button>
                        )}
                      </div>
                    </Col>
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <Button
                          className="btn btn-soft"
                          onClick={() => setModalOpen(true)}
                          disabled={loading}
                        >
                          <i className="ri-filter-3-line"></i>
                        </Button>
                        <div className="search-box ms-2 me-2">
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={t("Search")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>
                      </div>
                    </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
              
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    <CommonDataTable
                      data={categories}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
       <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitCategoryHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={AddCategoryForm}
        FormBodyProps={{ onTranslateClickHandler, translationLoading, lastTranslatedField }} 
        title={isAddOrEdit === "isAdd" ? t("AddNewCategory") : t("EditCategory")}
        size="lg"
      />
        

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />
    </div>
  );
}

export default Categorymanagement;
